import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Root component — uses existing global CSS classes to match the original
 * Backbone settings page layout exactly.
 */
import { useEffect, useState } from 'react';
import './styles.css';
import { GeneralPanel } from './components/GeneralPanel';
import { FeaturesPanel } from './components/FeaturesPanel';
import { DispositionsPanel } from './components/DispositionsPanel';
import { AdvancedOptionsPanel } from './components/AdvancedOptionsPanel';
import { useCurrentUser } from './hooks/useCurrentUser';
import { useOrganization } from './hooks/useOrganization';
import { useSettingsForm } from './hooks/useSettingsForm';
import { useSettingsStore } from './store/settings-store';
import { buildSettingsDiff } from './lib/buildSettingsDiff';
import { ConfirmChangesModal } from './components/ConfirmChangesModal';
export function App() {
    const { loading: userLoading, permissions } = useCurrentUser();
    const { org, loading: orgLoading, saving, error, save } = useOrganization();
    const form = useSettingsForm(org, permissions.canViewAll);
    const notify = useSettingsStore((s) => s.notify);
    const [pendingDiff, setPendingDiff] = useState(null);
    useEffect(() => {
        document.title = 'Scout | Settings';
        mixpanel.track('Settings View');
    }, []);
    if (userLoading || orgLoading) {
        return _jsx("div", { className: "settings-loading", children: "Loading settings..." });
    }
    if (!permissions.canAccess) {
        return _jsx("div", { className: "settings-loading", children: "You do not have permission to view this page." });
    }
    if (error && !org) {
        return _jsxs("div", { className: "settings-error", children: ["Failed to load settings: ", error] });
    }
    if (!form.isInitialized) {
        return _jsx("div", { className: "settings-loading", children: "Loading settings..." });
    }
    const currentDiff = buildSettingsDiff(org, form, permissions.canViewAll);
    const hasUnsavedChanges = currentDiff.length > 0;
    const handleUpdate = () => {
        const result = form.validate();
        const managementValid = form.wellTestManagementColumns.validate();
        if (!result.valid || !managementValid) {
            const hasDateError = result.errors.calcCutoff || result.errors.ticketClosingDate || result.errors.activeDate || result.errors.inactiveDate;
            const hasRequiredError = result.errors.days || result.errors.maxChars;
            const messages = [];
            if (hasRequiredError) {
                messages.push('Some required fields are missing.');
            }
            if (hasDateError) {
                messages.push('Date fields cannot be set for future dates.');
            }
            if (messages.length === 0) {
                messages.push('Please correct the highlighted errors before saving.');
            }
            notify('Error', messages.join(' '), 'error');
            return;
        }
        const diff = currentDiff;
        if (diff.length === 0) {
            notify('Notice', 'No changes detected.');
            return;
        }
        setPendingDiff(diff);
    };
    const handleApplyChanges = async () => {
        try {
            await save(form.getUpdates());
            await form.wellTestManagementColumns.save();
            setPendingDiff(null);
        }
        catch (_a) {
            notify('Error', 'Failed to save settings. Please try again.', 'error');
        }
    };
    const handleGoBack = () => {
        setPendingDiff(null);
    };
    const handleCancel = () => {
        form.reset();
        form.wellTestManagementColumns.reset();
    };
    const anySaving = saving || form.wellTestManagementColumns.saving;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "col-md-12 before-action-footer", style: { paddingBottom: 160 }, children: [_jsxs("div", { className: "page-title", children: [_jsx("h3", { className: "all-caps", children: _jsx("span", { className: "semi-bold", children: "Settings" }) }), _jsx("hr", {})] }), _jsx(GeneralPanel, { form: form }), permissions.canViewAll && _jsx(FeaturesPanel, { form: form }), permissions.canViewAll && _jsx(DispositionsPanel, { form: form }), permissions.canViewAll && _jsx(AdvancedOptionsPanel, { form: form })] }), _jsx("div", { className: "action-footer", children: _jsxs("div", { className: "pull-right", children: [_jsx("button", { type: "button", className: "btn btn-default", "data-test": "settings_cancel_button", onClick: handleCancel, disabled: !hasUnsavedChanges || anySaving, children: "Cancel" }), '\u00A0', _jsx("button", { type: "button", className: "btn btn-success", "data-test": "settings_update_button", onClick: handleUpdate, disabled: anySaving, children: anySaving ? 'Saving...' : 'Update' })] }) }), pendingDiff && _jsx(ConfirmChangesModal, { sections: pendingDiff, isApplying: anySaving, onGoBack: handleGoBack, onApply: handleApplyChanges })] }));
}
